import { ReactNode } from 'react';

import { CurrentPointOnboardingType, SelectPlayerFlowType } from 'application/components/common/SelectPlayerFlow/types';
import { NavigateFunction, RouterUtils } from 'application/providers/RouterProvider';
import { getStreamsCategoryPath, StreamsCategoryParamValues } from 'application/pages/StreamPage';
import { ModalTypes } from 'application/providers/ModalProvider';
import { POINTS_MENU_PATH } from 'application/consts';
import { TEXTS } from 'application/pages/BillingPage/renderer/texts';

import { AcceptanceAdvanceStatus, PaymentKind, PaymentMethodObjectPureType } from '@shared/api/graphql/generated';
import { DEFAULT_ERROR_MESSAGE } from '@shared/config';
import { toast } from '@shared/ui';

import { CURRENT_POINT_KEY, IS_NEED_REDIRECT_TO_SOURCE_FROM_KEY, ONBOARDING_FLOW } from './licensesModalStorageKeys';
import { persistCurrentPoint } from './persistCurrentPoint';
import { sendEvent } from './sendEvent';
import { redirectToBindCard } from './redirectToBindCard';
import { handleBindCardError } from './handleBindCardError';
import { URI_FOR_RETURN_CARD } from './createLicensesModalBindCard';

interface Props {
  companyId: string;
  navigate: NavigateFunction;
  setCurrentPoint: (currentPoint: CurrentPointOnboardingType) => void;
  currentPoint: CurrentPointOnboardingType;
  close: (count?: number) => void;
  openModal?: (type: ModalTypes, props: {}) => void;
  closeModalButton: ReactNode;
  unpaidLicensesOnly: boolean;
  isNeedPayment: boolean;
  hasTrialLicenses: boolean;
  hasInactiveLicenses: boolean;
  hasWaitPaymentLicenses: boolean;
  hasGraceLicenses: boolean;
  paymentMethod: PaymentMethodObjectPureType | null;
  selectedPaymentKind: PaymentKind;
  acceptanceAdvanceStatus: AcceptanceAdvanceStatus | null;
  toSuspendAutorenewal: string[];
  toEnableAutorenewal: string[];
  toEnableAutoRenewalPaid: string[];
  toEnableAutoRenewalUnpaid: string[];
  suspendAutoRenewal: (companyId: string, licensesIds: string[]) => Promise<unknown>;
  resumeAutoRenewal: (companyId: string, licensesIds: string[]) => Promise<unknown>;
  refetchBillingInfo: () => Promise<unknown>;
  getLinkForPayment: (uriForReturn: string, companyId: string, licensesIds: string[]) => Promise<string | undefined>;
}

const createLicensesModalSubmitFlow = ({
  companyId,
  navigate,
  setCurrentPoint,
  currentPoint,
  close,
  openModal,
  closeModalButton,
  unpaidLicensesOnly,
  isNeedPayment,
  hasTrialLicenses,
  hasInactiveLicenses,
  hasWaitPaymentLicenses,
  hasGraceLicenses,
  paymentMethod,
  selectedPaymentKind,
  acceptanceAdvanceStatus,
  toSuspendAutorenewal,
  toEnableAutorenewal,
  toEnableAutoRenewalPaid,
  toEnableAutoRenewalUnpaid,
  suspendAutoRenewal,
  resumeAutoRenewal,
  refetchBillingInfo,
  getLinkForPayment,
}: Props) => {
  const redirectToSuccessStep = () => {
    const onboardingFlowType = localStorage.getItem(ONBOARDING_FLOW);
    localStorage.removeItem(ONBOARDING_FLOW);

    if (onboardingFlowType === SelectPlayerFlowType.CHANGE_DEVICE) {
      navigate(RouterUtils.generatePath(POINTS_MENU_PATH, {}, { success: 'true' }));

      return;
    }

    const streamPath = getStreamsCategoryPath(StreamsCategoryParamValues.RECOMMENDED);
    navigate(RouterUtils.generatePath(streamPath, {}, { success: 'true' }));
  };

  const checkIfNeedRedirectToSourceFrom = () => {
    const currentPointString = localStorage.getItem(CURRENT_POINT_KEY);

    if (currentPointString && currentPointString !== 'undefined') {
      const persistedCurrentPoint = JSON.parse(currentPointString) as CurrentPointOnboardingType;
      setCurrentPoint({ ...persistedCurrentPoint, isEnabled: true });
      redirectToSuccessStep();
      close(2);
    }

    localStorage.removeItem(IS_NEED_REDIRECT_TO_SOURCE_FROM_KEY);
    localStorage.removeItem(CURRENT_POINT_KEY);
  };

  const openConfirmPayModal = () => {
    openModal?.(ModalTypes.BILLING_EXTEND_SUBSCRIPTION, {
      licenses: toEnableAutoRenewalUnpaid,
      submitCallback: () => {
        toast.info(
          selectedPaymentKind === PaymentKind.Sbbol
            ? TEXTS.BILLING_AWAITING_SBBOL_TEXT
            : TEXTS.BILLING_AWAITING_CARD_TEXT
        );

        checkIfNeedRedirectToSourceFrom();
      },
    });
  };

  const bindCardAndPayConfirm = async (licensesIds: string[]) => {
    try {
      const link = await getLinkForPayment(URI_FOR_RETURN_CARD, companyId, licensesIds);

      redirectToBindCard(link);
    } catch (e) {
      await handleBindCardError(e as Error, refetchBillingInfo);
    }
  };

  const toPayUnpaidLicenses = async () => {
    try {
      if (
        selectedPaymentKind === PaymentKind.Sbbol &&
        (acceptanceAdvanceStatus === AcceptanceAdvanceStatus.Active ||
          acceptanceAdvanceStatus === AcceptanceAdvanceStatus.Authorized)
      ) {
        openConfirmPayModal();

        return;
      }

      if (!paymentMethod || hasWaitPaymentLicenses || hasGraceLicenses) {
        await bindCardAndPayConfirm(toEnableAutoRenewalUnpaid);
      } else {
        openConfirmPayModal();
      }
    } catch (e) {
      toast.error(DEFAULT_ERROR_MESSAGE);
    }
  };

  const changeAutoRenewal = async () => {
    if (toSuspendAutorenewal.length > 0) {
      await suspendAutoRenewal(companyId, toSuspendAutorenewal);
    }

    if (toEnableAutorenewal.length > 0) {
      await resumeAutoRenewal(companyId, toEnableAutoRenewalPaid);
    }

    await refetchBillingInfo();

    const isNeedStopSubscription = toEnableAutoRenewalUnpaid.length === 0 && toSuspendAutorenewal.length > 0;

    const isNeedStartSubscription =
      toEnableAutoRenewalUnpaid.length === 0 && toEnableAutoRenewalPaid.length > 0 && toSuspendAutorenewal.length === 0;

    if (isNeedStopSubscription) {
      openModal?.(ModalTypes.CUSTOM, {
        title: TEXTS.BILLING_AUTORENEWAL_SUSPENDED_TITLE,
        description: TEXTS.BILLING_AUTORENEWAL_SUSPENDED_DESC,
        actionButtons: [closeModalButton],
      });
    }

    if (isNeedStartSubscription) {
      openModal?.(ModalTypes.CUSTOM, {
        title: TEXTS.BILLING_SUBMIT_CHANGE_AUTORENEWABLE_TITLE,
        description: TEXTS.BILLING_SUBMIT_CHANGE_AUTORENEWABLE_SUCCESS,
        actionButtons: [closeModalButton],
      });

      if (hasTrialLicenses) {
        toast.info(TEXTS.BILLING_SUBMIT_CHANGE_AUTORENEWABLE_TRIAL);
      }
    }
  };

  const handleChangeAutoRenewal = async () => {
    sendEvent(unpaidLicensesOnly, isNeedPayment);

    try {
      await changeAutoRenewal();

      if (toEnableAutoRenewalUnpaid.length > 0) {
        await toPayUnpaidLicenses();
      }
    } catch (e) {
      toast.error(TEXTS.BILLING_SUBMIT_CHANGE_AUTORENEWABLE_ERROR);
    }
  };

  const handleChangeAutoRenewalUnpaid = async () => {
    try {
      await changeAutoRenewal();

      if (hasInactiveLicenses && !paymentMethod && currentPoint && selectedPaymentKind === PaymentKind.Yookassa) {
        persistCurrentPoint(currentPoint);
      }

      await toPayUnpaidLicenses();
    } catch (e) {
      toast.error(TEXTS.BILLING_SUBMIT_CHANGE_AUTORENEWABLE_ERROR);
    }
  };

  return {
    handleChangeAutoRenewal: unpaidLicensesOnly ? handleChangeAutoRenewalUnpaid : handleChangeAutoRenewal,
  };
};

export default createLicensesModalSubmitFlow;
